import { useMemo } from "preact/hooks";
import fuzzysort from "fuzzysort";
import type { Category } from "../types";
import { useEdition } from "../hooks/useEdition";
import { useTranslations } from "../hooks/useTranslations";
import { useArtists } from "../hooks/useWidgetQueries";
import { useNavigation } from "../lib/router";
import { useUrlStringState, useUrlArrayState } from "../hooks/useUrlState";
import { getLocalizedName } from "../lib/localization";
import { hasAnySelectedCategory, matchesConfiguredCategory } from "../lib/categoryFilter";
import { ArtistCard } from "../components/artists/ArtistCard";
import { EmptyState } from "../components/ui/EmptyState";
import { FilterDropdown } from "../components/ui/FilterDropdown";
import { SearchInput } from "../components/ui/SearchInput";
import { DaySelector } from "../components/ui/DaySelector";
import { ArtistCardSkeleton, DaySelectorSkeleton, FilterDropdownSkeleton } from "../components/ui/Skeleton";

type ArtistsPageProps = {
  onNavigate: (path: string, elementId?: string) => void;
};

export function ArtistsPage({ onNavigate }: ArtistsPageProps) {
  const {
    edition,
    editionId,
    locale,
    lockedCategoryIds,
    categoryName: configCategoryName,
  } = useEdition();
  const t = useTranslations();
  const { toArtist } = useNavigation();
  const { data: artists, loading } = useArtists(editionId);

  const [search, setSearch] = useUrlStringState("search", "");
  const [selectedDay, setSelectedDay] = useUrlStringState("day", null);
  const [selectedCategories, setSelectedCategories] = useUrlArrayState("categories", []);

  // Get unique days from artists shown by this block.
  const days = useMemo(() => {
    if (!artists) return [];

    const scheduledDays = new Set<string>();

    for (const artist of artists) {
      if (lockedCategoryIds.length > 0) {
        if (!hasAnySelectedCategory(artist.categories, lockedCategoryIds)) {
          continue;
        }
      } else if (configCategoryName) {
        if (
          !artist.categories.some((cat) =>
            matchesConfiguredCategory(
              cat,
              configCategoryName,
              locale,
              edition?.default_locale_code
            )
          )
        ) {
          continue;
        }
      }

      for (const schedule of artist.schedules) {
        scheduledDays.add(schedule.start_date);
      }
    }

    return Array.from(scheduledDays).sort();
  }, [artists, lockedCategoryIds, configCategoryName, locale, edition]);

  const activeDay = selectedDay && days.includes(selectedDay) ? selectedDay : null;

  // Get unique categories from all artists
  const categories = useMemo(() => {
    if (!artists) return [];
    const catMap = new Map<string, Category>();
    for (const artist of artists) {
      for (const cat of artist.categories) {
        if (!catMap.has(cat._id)) {
          catMap.set(cat._id, cat as Category);
        }
      }
    }
    return Array.from(catMap.values()).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
  }, [artists]);

  // Category options for FilterDropdown
  const categoryOptions = useMemo(() => {
    return categories.map((cat) => ({
      id: cat._id,
      label: getLocalizedName(cat, locale, edition?.default_locale_code) ?? cat.name ?? "",
    }));
  }, [categories, locale, edition?.default_locale_code]);

  // Filter artists
  const filteredArtists = useMemo(() => {
    if (!artists) return [];

    // Pre-filter by category and day
    let filtered = artists.filter((artist) => {
      // Block-configured category filter takes precedence (filter by ID, OR)
      if (lockedCategoryIds.length > 0) {
        if (!hasAnySelectedCategory(artist.categories, lockedCategoryIds)) {
          return false;
        }
      } else if (configCategoryName) {
        // Legacy fallback for older blocks/shortcodes using category name
        if (
          !artist.categories.some((cat) =>
            matchesConfiguredCategory(
              cat,
              configCategoryName,
              locale,
              edition?.default_locale_code
            )
          )
        ) {
          return false;
        }
      } else if (selectedCategories.length > 0) {
        // URL category filter (multi-select by ID)
        if (!hasAnySelectedCategory(artist.categories, selectedCategories)) {
          return false;
        }
      }

      // Day filter
      if (activeDay !== null) {
        const hasScheduleOnDay = artist.schedules.some((schedule) => {
          // Artist list schedules only have start_date
          return schedule.start_date === activeDay;
        });
        if (!hasScheduleOnDay) {
          return false;
        }
      }

      return true;
    });

    // Apply fuzzy search
    if (search) {
      const results = fuzzysort.go(search, filtered, {
        key: "name",
        threshold: -10000,
      });
      return results.map((r) => r.obj);
    }

    return filtered;
  }, [artists, search, selectedCategories, activeDay, lockedCategoryIds, configCategoryName, locale, edition]);

  // Check if any filters are active
  const hasActiveFilters = (search && search.length > 0) || activeDay !== null || selectedCategories.length > 0;

  // Clear all filters
  const clearFilters = () => {
    setSearch(null);
    setSelectedDay(null);
    setSelectedCategories([]);
  };

  if (loading) {
    return (
      <div className="festapp-artists-page">
        <div className="festapp-artists-filters">
          <SearchInput value="" onChange={() => {}} placeholder={t.searchArtists} disabled />
          <div className="festapp-artists-filter-row">
            <DaySelectorSkeleton />
            <FilterDropdownSkeleton />
          </div>
        </div>
        <div className="festapp-artists-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <ArtistCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="festapp-artists-page">
      <div className="festapp-artists-filters">
        <SearchInput
          value={search ?? ""}
          onChange={(v) => setSearch(v || null)}
          placeholder={t.searchArtists}
        />
        <div className="festapp-artists-filter-row">
          <DaySelector
            days={days}
            selectedDay={activeDay}
            onChange={setSelectedDay}
          />
          {categories.length > 0 && lockedCategoryIds.length === 0 && !configCategoryName && (
            <FilterDropdown
              label={t.categories}
              options={categoryOptions}
              selected={selectedCategories}
              onChange={setSelectedCategories}
            />
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className="festapp-btn festapp-btn-ghost festapp-btn-sm"
              onClick={clearFilters}
            >
              {t.clearFilters}
            </button>
          )}
        </div>
      </div>

      {filteredArtists.length === 0 ? (
        <EmptyState
          message={t.noArtistsFound}
          onClearFilters={hasActiveFilters ? clearFilters : undefined}
        />
      ) : (
        <div className="festapp-artists-grid">
          {filteredArtists.map((artist) => (
            <ArtistCard
              key={artist._id}
              artist={artist}
              onClick={() => onNavigate(toArtist(artist._id), artist._id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
